import React, { useState } from "react";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { submitFeedback } from "@/lib/api";

const MOODS = [
    { id: "loved", label: "loved it", emoji: "🍡" },
    { id: "okay", label: "it was okay", emoji: "🍵" },
    { id: "rough", label: "rough week", emoji: "🌧️" },
];

const srcFromUrl = () => {
    try { return new URLSearchParams(window.location.search).get("src") || "app"; }
    catch { return "app"; }
};

// Standalone /feedback page — linked from the day-7 wrap-up email.
export default function FeedbackPage() {
    const [mood, setMood] = useState(null);
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (message.trim().length < 3) {
            toast.error("a few more words? even two sentences helps 🍡");
            return;
        }
        setBusy(true);
        try {
            await submitFeedback(message.trim(), mood, srcFromUrl());
            setDone(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "that didn't send — try again in a sec?");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen font-body flex items-center justify-center px-5 py-12" data-testid="feedback-page">
            <Toaster position="top-center" richColors={false} />
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 22 }}
                className="w-full max-w-md"
            >
                <div className="text-center mb-6">
                    <div className="text-5xl mb-3">🍡</div>
                    <h1 className="font-display text-3xl text-warm-900">tell Mochi how it went</h1>
                    <p className="text-warm-500 text-sm mt-2">
                        what helped, what got in the way, what you wish i did. no wrong answers 🌸
                    </p>
                </div>

                {done ? (
                    <div className="bg-cream rounded-clay shadow-clay border border-warm-300/30 p-8 text-center" data-testid="feedback-thanks">
                        <div className="text-4xl mb-3">🥺</div>
                        <h2 className="font-display text-xl text-warm-900 mb-2">got it. thank you.</h2>
                        <p className="text-warm-500 text-sm mb-6">i read every one of these. see you tomorrow?</p>
                        <a href="/" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover transition-colors" data-testid="feedback-back-home">
                            <ArrowLeft className="w-4 h-4" /> back to Mochi
                        </a>
                    </div>
                ) : (
                    <form onSubmit={submit} className="bg-cream rounded-clay shadow-clay border border-warm-300/30 p-6 space-y-5" data-testid="feedback-form">
                        <div>
                            <p className="text-warm-500 text-xs uppercase tracking-wide font-semibold mb-2">how did the week feel?</p>
                            <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="mood">
                                {MOODS.map((m) => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={mood === m.id}
                                        onClick={() => setMood(mood === m.id ? null : m.id)}
                                        className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${mood === m.id ? "bg-terracotta text-cream" : "bg-surface text-warm-800 hover:bg-surface-hover"}`}
                                        data-testid={`feedback-mood-${m.id}`}
                                    >
                                        {m.emoji} {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label htmlFor="feedback-message" className="text-warm-500 text-xs uppercase tracking-wide font-semibold mb-2 block">your note</label>
                            <textarea
                                id="feedback-message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                maxLength={2000}
                                rows={5}
                                placeholder="the morning check-in was my favourite… the reminders felt a bit much on tuesday…"
                                className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/40 resize-none"
                                data-testid="feedback-message-input"
                            />
                            <p className="text-warm-500 text-[11px] text-right mt-1">{message.length}/2000</p>
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover disabled:opacity-60 transition-colors"
                            data-testid="feedback-submit-btn"
                        >
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} send to Mochi
                        </button>
                        <a href="/" className="block text-center text-warm-500 text-xs hover:text-warm-900 transition-colors" data-testid="feedback-skip-link">
                            not now — back to Mochi
                        </a>
                    </form>
                )}
            </motion.div>
        </div>
    );
}
